import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaCopy, FaCheck, FaEye, FaVideo, FaSyncAlt } from 'react-icons/fa';
import TopPageNavigationBand from './TopPageNavigationBand.jsx';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { getCurrentUserId } from '../_helpers/bandUtils.js';
import config from '../config.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { RichMessage } from '../_helpers/richMessage.jsx';
import '../styles/OBSSetupPage.css';

const OVERLAY_WIDTH = 1920;
const OVERLAY_HEIGHT = 1080;

const COPY_RESET_MS = 2500;

// The viewer_role values (from GET /bands/{id}) that may read the overlay
// token. The token route is RequireMemberOrModerator, so a moderator who
// isn't a member of the band may run its stream too (ENG-584).
const TOKEN_ROLES = new Set(['member', 'moderator']);

const isBandAccessDenied = (err) => err?.status === 403 && err?.code === 'band_access_denied';
const isBandRoleRequired = (err) => err?.status === 403 && err?.code === 'band_role_required';

const OBSSetupPage = () => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const fetchWrapperRef = useRef(useFetchWrapper());
  const t = useT();

  const [band, setBand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  // The band's overlay token (ENG-497). The overlay URL used to carry the
  // session JWT, which grants full account access; the overlay token grants
  // read-only access to what's playing, and regenerating it cuts off every
  // URL made from the old one.
  const [overlayToken, setOverlayToken] = useState(null);
  const [tokenError, setTokenError] = useState(false);
  // Whether this viewer may rotate the token: can_regenerate on the token
  // response, true only for the owner and moderators (ENG-578). Anything but
  // an explicit true (a missing field, a token that failed to load) hides
  // the button.
  const [canRegenerate, setCanRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // After a regenerate: 'regenerated', 'regenerateFailed' (refused for the
  // viewer's role), 'regenerateFailedGeneric' (anything else); else null.
  const [regenerateResult, setRegenerateResult] = useState(null);

  const overlayUrl = overlayToken
    ? `${window.location.origin}/obs/bands/${bandid}?overlay=${encodeURIComponent(overlayToken)}`
    : '';

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const fetchBand = async () => {
      try {
        setIsLoading(true);
        const bandData = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}`);
        // Members and moderators may read the token; followers and everyone
        // else go to the band page. A backend that predates viewer_role
        // omits it, and the token route decides instead, below.
        const viewerRole = bandData?.viewer_role;
        if (viewerRole && !TOKEN_ROLES.has(viewerRole)) {
          navigate(`/performers/${bandid}`, { replace: true });
          return;
        }
        let tokenData = null;
        try {
          tokenData = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/overlay/token`);
        } catch (err) {
          // The token route's guard turned the viewer away: neither a member
          // nor a moderator of this band.
          if (isBandAccessDenied(err)) {
            navigate(`/performers/${bandid}`, { replace: true });
            return;
          }
        }
        setBand(bandData);
        if (tokenData?.token) {
          setOverlayToken(tokenData.token);
          setCanRegenerate(tokenData.can_regenerate === true);
        } else {
          setTokenError(true);
          setCanRegenerate(false);
        }
      } catch (_err) {
        setError('common.errors.loadBand');
      } finally {
        setIsLoading(false);
      }
    };

    if (!bandid) return;
    // Signed out: the token needs a session. isBandMember used to send these
    // viewers to the band page, and still does.
    if (!getCurrentUserId()) {
      navigate(`/performers/${bandid}`, { replace: true });
      return;
    }
    fetchBand();
  }, [bandid, navigate]);

  const handleRegenerate = async () => {
    if (regenerating) return;
    if (!window.confirm(t('obs.url.regenerateConfirm'))) return;
    setRegenerating(true);
    setRegenerateResult(null);
    try {
      const tokenData = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandid}/overlay/token/regenerate`);
      if (!tokenData?.token) throw new Error('no overlay token');
      setOverlayToken(tokenData.token);
      setCanRegenerate(tokenData.can_regenerate === true);
      setTokenError(false);
      setCopied(false);
      setRegenerateResult('regenerated');
    } catch (err) {
      // Refused for the viewer's role: say who can, and stop offering it.
      // Anything else may pass on a retry, so the button stays.
      if (isBandRoleRequired(err)) {
        setCanRegenerate(false);
        setRegenerateResult('regenerateFailed');
      } else {
        setRegenerateResult('regenerateFailedGeneric');
      }
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!overlayUrl) return;
    let copySucceeded = false;
    try {
      await navigator.clipboard.writeText(overlayUrl);
      copySucceeded = true;
    } catch (_e) {
      const el = document.createElement('textarea');
      el.value = overlayUrl;
      document.body.appendChild(el);
      el.select();
      copySucceeded = document.execCommand('copy');
      document.body.removeChild(el);
    }
    if (!copySucceeded) return;
    setCopied(true);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), COPY_RESET_MS);
  };

  const handleBack = () => navigate(`/performers/${bandid}/streamer`);

  if (isLoading) {
    return (
      <div className="obs-setup-page">
        <TopPageNavigationBand showHamburgerMenu={false} />
        <div className="obs-setup-loading" aria-live="polite">
          <p>{t('common.status.loadingEllipsis')}</p>
        </div>
      </div>
    );
  }

  if (error || !band) {
    return (
      <div className="obs-setup-page">
        <TopPageNavigationBand showHamburgerMenu={false} />
        <div className="obs-setup-error">
          <p>{t(error || 'common.errors.bandNotFound')}</p>
          <button className="obs-back-btn" onClick={handleBack}>
            <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="obs-setup-page">
      <TopPageNavigationBand showHamburgerMenu={false} />

      <div className="obs-setup-titlebar">
        <button className="obs-back-btn" onClick={handleBack} aria-label={t('obs.backToDashboard')}>
          <FaArrowLeft className="rtl-mirror" />
        </button>
        <h1 className="obs-setup-title">
          <FaVideo className="obs-setup-title-icon" aria-hidden="true" />
          {t('obs.title')}
        </h1>
      </div>

      <main className="obs-setup-content">
        {/* Overlay URL */}
        <section className="obs-setup-card" aria-label={t('obs.url.title')}>
          <h2 className="obs-setup-card-title">{t('obs.url.title')}</h2>
          <p className="obs-setup-card-desc">
            <RichMessage
              message={t('obs.url.description')}
              parts={{ browserSource: <strong>{t('obs.terms.browserSource')}</strong> }}
            />
          </p>
          <div className="obs-url-row">
            <input
              className="obs-url-input"
              type="text"
              readOnly
              value={overlayUrl}
              placeholder={tokenError ? '' : t('common.status.loadingEllipsis')}
              aria-label={t('obs.url.title')}
              onClick={(e) => e.target.select()}
            />
            <button
              className={`obs-copy-btn${copied ? ' obs-copy-btn--copied' : ''}`}
              onClick={handleCopy}
              disabled={!overlayUrl}
              aria-label={copied ? t('obs.url.copied') : t('obs.url.copy')}
              title={copied ? t('common.actions.copied') : t('obs.url.copyTitle')}
            >
              {copied ? <FaCheck /> : <FaCopy />}
              {copied ? t('common.actions.copied') : t('common.actions.copy')}
            </button>
          </div>
          {tokenError && (
            <p className="obs-url-message obs-url-message--error" role="alert">{t('obs.url.loadFailed')}</p>
          )}
          {canRegenerate && (
            <div className="obs-url-actions">
              <button
                type="button"
                className="obs-regenerate-btn"
                onClick={handleRegenerate}
                disabled={regenerating}
              >
                <FaSyncAlt aria-hidden="true" />
                {regenerating ? t('obs.url.regenerating') : t('obs.url.regenerate')}
              </button>
            </div>
          )}
          {regenerateResult && (
            <p
              className={`obs-url-message${regenerateResult !== 'regenerated' ? ' obs-url-message--error' : ''}`}
              role={regenerateResult !== 'regenerated' ? 'alert' : 'status'}
            >
              {t(`obs.url.${regenerateResult}`)}
            </p>
          )}
        </section>

        {/* Recommended OBS settings */}
        <section className="obs-setup-card" aria-label={t('obs.settings.region')}>
          <h2 className="obs-setup-card-title">{t('obs.settings.title')}</h2>
          <div className="obs-settings-grid">
            <div className="obs-setting-item">
              <span className="obs-setting-label">{t('obs.settings.width')}</span>
              <span className="obs-setting-value">{t('obs.settings.pixels', { value: OVERLAY_WIDTH })}</span>
            </div>
            <div className="obs-setting-item">
              <span className="obs-setting-label">{t('obs.settings.height')}</span>
              <span className="obs-setting-value">{t('obs.settings.pixels', { value: OVERLAY_HEIGHT })}</span>
            </div>
            <div className="obs-setting-item">
              <span className="obs-setting-label">{t('obs.settings.fps')}</span>
              <span className="obs-setting-value">30</span>
            </div>
            <div className="obs-setting-item obs-setting-item--full">
              <span className="obs-setting-label">{t('obs.settings.customCss')}</span>
              <code className="obs-setting-code">
                {'body { background-color: transparent; margin: 0; }'}
              </code>
            </div>
          </div>
        </section>

        {/* Step-by-step instructions */}
        <section className="obs-setup-card" aria-label={t('obs.steps.region')}>
          <h2 className="obs-setup-card-title">{t('obs.steps.title')}</h2>
          <ol className="obs-steps-list">
            <li className="obs-step">
              <span className="obs-step-num" aria-hidden="true">1</span>
              <span className="obs-step-text">
                <RichMessage
                  message={t('obs.steps.addSource')}
                  parts={{ plus: <strong>+</strong>, sources: <strong>{t('obs.terms.sources')}</strong> }}
                />
              </span>
            </li>
            <li className="obs-step">
              <span className="obs-step-num" aria-hidden="true">2</span>
              <span className="obs-step-text">
                <RichMessage
                  message={t('obs.steps.selectBrowser')}
                  parts={{ browser: <strong>{t('obs.terms.browser')}</strong> }}
                />
              </span>
            </li>
            <li className="obs-step">
              <span className="obs-step-num" aria-hidden="true">3</span>
              <span className="obs-step-text">
                <RichMessage
                  message={t('obs.steps.pasteUrl')}
                  parts={{ urlField: <strong>{t('obs.terms.url')}</strong> }}
                />
              </span>
            </li>
            <li className="obs-step">
              <span className="obs-step-num" aria-hidden="true">4</span>
              <span className="obs-step-text">
                <RichMessage
                  message={t('obs.steps.setSize')}
                  parts={{
                    width: <strong>{t('obs.settings.width')}</strong>,
                    widthValue: <strong>{OVERLAY_WIDTH}</strong>,
                    height: <strong>{t('obs.settings.height')}</strong>,
                    heightValue: <strong>{OVERLAY_HEIGHT}</strong>,
                  }}
                />
              </span>
            </li>
            <li className="obs-step">
              <span className="obs-step-num" aria-hidden="true">5</span>
              <span className="obs-step-text">
                <RichMessage
                  message={t('obs.steps.pasteCss')}
                  parts={{
                    customCss: <strong>{t('obs.settings.customCss')}</strong>,
                    css: (
                      <code className="obs-inline-code">
                        {'body { background-color: transparent; margin: 0; }'}
                      </code>
                    ),
                  }}
                />
              </span>
            </li>
            <li className="obs-step">
              <span className="obs-step-num" aria-hidden="true">6</span>
              <span className="obs-step-text">
                <RichMessage
                  message={t('obs.steps.confirm')}
                  parts={{ ok: <strong>{t('common.actions.ok')}</strong> }}
                />
              </span>
            </li>
          </ol>
        </section>

        {/* Visual preview mockup */}
        <section className="obs-setup-card obs-setup-card--preview" aria-label={t('obs.preview.region')}>
          <h2 className="obs-setup-card-title">
            <FaEye className="obs-card-icon" aria-hidden="true" />
            {t('obs.preview.title')}
          </h2>
          <p className="obs-setup-card-desc">
            {t('obs.preview.description')}
          </p>
          <div className="obs-preview-mockup" aria-label={t('obs.preview.mockup')}>
            <div className="obs-preview-canvas">
              <span className="obs-preview-canvas-label">{t('obs.preview.canvas', { width: OVERLAY_WIDTH, height: OVERLAY_HEIGHT })}</span>
              <div className="obs-preview-overlay-sample" aria-hidden="true">
                <span className="obs-preview-tag">{t('obs.overlay.nowPlaying')}</span>
                <span className="obs-preview-song-title">{t('obs.preview.sampleTitle')}</span>
                <span className="obs-preview-artist">{t('obs.preview.sampleArtist')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Security notice */}
        <section className="obs-setup-card obs-setup-card--note" aria-label={t('obs.security.region')}>
          <p className="obs-note-text">
            <RichMessage message={t('obs.security.text')} parts={{ label: <strong>{t('obs.security.label')}</strong> }} />
          </p>
        </section>
      </main>
    </div>
  );
};

export default OBSSetupPage;
